<template>
    <div class="wizard-forms vrm-dess-wizard__grid">
        <div class="row ">
            <div class="col-md-10 col-md-offset-1">
                <h2>{{ $t('dynamicess.wizard.grid') }}</h2>
                <p>
                    {{
                        $t('dynamicess.gridDescription', {
                            i18nFallback:
                                'Dynamic ESS needs to know the limitations of your grid connection to accurately schedule grid usage.',
                        })
                    }}
                </p>
                <br />

                <div class="vrm-form__fieldset-item vrm-form__fieldset-item--checkbox">
                    <label class="sg-textfield-label">
                        <span class="sg-textfield-label-caption">
                            {{
                                $t('ess.energy_sell_question', {
                                    i18nFallback: 'Can you sell energy back to the grid?',
                                })
                            }}
                        </span>
                    </label>
                    <div
                        class="vrm-form__custom-radio-checkbox vrm-form__custom-radio-checkbox--inline"
                    >
                        <div
                            v-for="sellType in Object.values(ENERGY_SELL_OPTIONS)"
                            :key="sellType"
                            class="sg-custom-check-and-radio custom-radio "
                            :class="{ active: energyGridSell === sellType }"
                            @click="setEnergyGridSell(sellType)"
                        >
                            <label>
                                <input type="radio" :checked="energyGridSell === sellType" />
                                <div class="sg-alternative-display">
                                    <div class="sg-outer">
                                        <div class="sg-inner"></div>
                                    </div>
                                </div>
                            </label>
                            <label for="energyGridSell">
                                {{ $t('ess.energy_sell_type.' + sellType) }}
                            </label>
                            <InputError v-show="errors.energyGridSell"></InputError>
                        </div>
                    </div>
                </div>
                <br />
                <div v-if="energyGridSell !== null" class="row">
                    <div class="col-xs-12 col-sm-6 col-lg-6">
                        <vrm-number-field
                            v-model="maxImport"
                            :checks="[NOT_NEGATIVE_NUMBER]"
                            :required="true"
                            :data-indicator="$t('ess.kw', { i18nFallback: 'kW' })"
                            :title="
                                $t('dynamicess.maxImport', {
                                    i18nFallback: 'Maximum import power',
                                })
                            "
                            :tooltip="
                                $t('dynamicess.maxImportTooltip', {
                                    i18nFallback:
                                        'Maximum power that your grid connection allows to be imported from the grid',
                                })
                            "
                        />
                    </div>
                    <div
                        v-if="energyGridSell == ENERGY_SELL_OPTIONS.YES"
                        class="col-xs-12 col-sm-6 col-lg-6"
                    >
                        <vrm-number-field
                            v-model="maxExport"
                            :checks="[NOT_NEGATIVE_NUMBER]"
                            :required="true"
                            :data-indicator="$t('ess.kw', { i18nFallback: 'kW' })"
                            :title="
                                $t('dynamicess.maxExport', {
                                    i18nFallback: 'Maximum export power',
                                })
                            "
                            :tooltip="
                                $t('dynamicess.maxExportTooltip', {
                                    i18nFallback:
                                        'Maximum power that your grid connection allows to be exported into the grid',
                                })
                            "
                        ></vrm-number-field>
                    </div>
                </div>
                <br />
                <div>
                    <label class="sg-textfield-label">
                        <span class="sg-textfield-label-caption">
                            {{
                                $t('dynamicess.operatingMode', {
                                    i18nFallback: 'Operating mode',
                                })
                            }}
                        </span>
                        <i
                            v-if="operatingModeTooltip !== null"
                            v-tooltip.bottom="operatingModeTooltip"
                            class="sg-vrm-icon_061_info-round-16px"
                        ></i>
                    </label>

                    <div class="vrm-dess-wizard__mode-picker">
                        <div
                            class="vrm-form__image-select vrm-form__image-select--small"
                            :class="{ active: isGreenModeOn === true }"
                            @click="setGreenMode(true)"
                        >
                            <div
                                class="vrm-form__image-select__img sg-vrm-icon_126_mode-green"
                            ></div>

                            <div class="vrm-form__image-select__text">
                                {{
                                    $t('dynamicess.modeGreen', {
                                        i18nFallback: 'Green mode',
                                    })
                                }}
                            </div>
                            <div
                                v-if="greenModePerks.length > 0"
                                class="vrm-form__image-select__subtext"
                            >
                                <ul>
                                    <li v-for="perk in greenModePerks" :key="perk">
                                        {{ perk }}
                                    </li>
                                </ul>
                            </div>
                        </div>

                        <div
                            class="vrm-form__image-select vrm-form__image-select--small"
                            :class="{ active: isGreenModeOn === false }"
                            @click="setGreenMode(false)"
                        >
                            <div
                                class="vrm-form__image-select__img sg-vrm-icon_127_mode-trade"
                            ></div>

                            <div class="vrm-form__image-select__text">
                                {{
                                    $t('dynamicess.modeTrade', {
                                        i18nFallback: 'Trade mode',
                                    })
                                }}
                            </div>
                            <div
                                v-if="tradeModePerks.length > 0"
                                class="vrm-form__image-select__subtext"
                            >
                                <ul>
                                    <li v-for="perk in tradeModePerks" :key="perk">
                                        {{ perk }}
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { Vue, Component, Prop } from 'vue-property-decorator';
import VrmNumberField from '@/vue/components/forms/VrmNumberField.vue';
import InputError from '@/vue/components/forms/InputError.vue';
import { DYNAMIC_ESS, DYNAMIC_ESS_FORM_FIELDS } from '@/vue/store/modules/dynamic-ess-form.js';
import { NOT_NEGATIVE_NUMBER } from '@/vue/constants/validation-checks';
import { CURRENCIES } from '@/vue/store/modules/currencies';

// Keys for the Green mode perk translations.
// No fallbacks needed since we filter out any terms that are not set.
const _greenModePerks = [
    'dynamicess.modeGreenPerk1',
    'dynamicess.modeGreenPerk2',
    'dynamicess.modeGreenPerk3',
];

// Keys for the Trade mode perk translations.
// No fallbacks needed since we filter out any terms that are not set.
const _tradeModePerks = [
    'dynamicess.modeTradePerk1',
    'dynamicess.modeTradePerk2',
    'dynamicess.modeTradePerk3',
];

@Component({
    components: {
        VrmNumberField,
        InputError,
    },
})
export default class Grid extends Vue {
    NOT_NEGATIVE_NUMBER = NOT_NEGATIVE_NUMBER;
    ENERGY_SELL_OPTIONS = {
        YES: 1,
        NO: 0,
    };
    errors = {
        energyGridSell: false,
    };
    @Prop()
    initialValues;

    changedField = new Map();
    creating = false;

    get energyGridSell() {
        return this.$store.state.dynamicEssForm.energyGridSell;
    }

    setEnergyGridSell(type) {
        this.compareAndEmitEdit('energyGridSell', type);
        this.$store.commit(DYNAMIC_ESS.SET_FORM_FIELD, {
            value: type,
            key: DYNAMIC_ESS_FORM_FIELDS.energyGridSell,
        });
    }

    get maxImport() {
        return this.$store.state.dynamicEssForm.maximumImportPower;
    }

    set maxImport(value) {
        this.compareAndEmitEdit('maximumImportPower', value);
        this.$store.commit(DYNAMIC_ESS.SET_FORM_FIELD, {
            value,
            key: DYNAMIC_ESS_FORM_FIELDS.maximumImportPower,
        });
    }

    get maxExport() {
        return this.$store.state.dynamicEssForm.maximumExportPower;
    }

    set maxExport(value) {
        this.compareAndEmitEdit('maximumExportPower', value);
        this.$store.commit(DYNAMIC_ESS.SET_FORM_FIELD, {
            value,
            key: DYNAMIC_ESS_FORM_FIELDS.maximumExportPower,
        });
    }

    get isGreenModeOn() {
        return this.$store.state.dynamicEssForm.isGreenModeOn;
    }

    setGreenMode(value) {
        this.compareAndEmitEdit('isGreenModeOn', value);
        this.$store.commit(DYNAMIC_ESS.SET_FORM_FIELD, {
            value: value,
            key: DYNAMIC_ESS_FORM_FIELDS.isGreenModeOn,
        });
    }

    /**
     * Returns any existing translations for the Green mode perks.
     *
     * @returns {*[]}
     */
    get greenModePerks() {
        return this.getExistingTranslations(_greenModePerks);
    }

    /**
     * Returns any existing translations for the Trade mode perks.
     *
     * @returns {*[]}
     */
    get tradeModePerks() {
        return this.getExistingTranslations(_tradeModePerks);
    }

    /**
     * Returns any translations for terms that exist and aren't empty.
     *
     * Used to filter any bullet points that should be shown for the operation mode picker.
     *
     * @param terms
     * @returns {*[]}
     */
    getExistingTranslations(terms) {
        let perks = [];

        for (let i in terms) {
            const term = terms[i];

            const text = this.$t(terms[i], {
                i18nFallback: null,
            });

            if (text !== null && text.length > 0 && text !== term) {
                perks.push(text);
            }
        }

        return perks;
    }

    /**
     * Return the tooltip message for the Operating mode if it exists.
     *
     * If the translation is left empty, return null.
     *
     * @returns {VueI18n.TranslateResult|null}
     */
    get operatingModeTooltip() {
        const key = 'dynamicess.operatingModeTooltip';

        const text = this.$t(key, {
            i18nFallback: null,
        });

        if (text !== key && text !== null && text.length > 0) {
            return text;
        }

        return null;
    }

    created() {
        let fields = [
            'energyGridSell',
            'maximumImportPower',
            'maximumExportPower',
            'isGreenModeOn',
        ];
        let initialValuesAllNull = true;
        fields.forEach(value => {
            this.changedField.set(value, false);
            if (this.initialValues[value] !== null) {
                initialValuesAllNull = false;
            }
        });
        this.creating = initialValuesAllNull;
    }

    compareAndEmitEdit(field, newValue) {
        let hasChange = false;
        // When making changes and energy grid sell set to 1 and sell form is invalid, view it as creating stage
        if (field === 'energyGridSell' && newValue === 1) {
            let sellFormInValid = !this.$store.getters[DYNAMIC_ESS.SELL_VALID];
            if (sellFormInValid) {
                this.creating = true;
            }
        }
        // Change it back to edit stage
        if (field === 'energyGridSell' && newValue === 0 && this.initialValues[field] === 0) {
            let gridFormValid = this.$store.getters[DYNAMIC_ESS.GRID_VALID];
            let batteryFormValid = this.$store.getters[DYNAMIC_ESS.BATTERY_VALID](
                this.$store.getters[CURRENCIES.GET_ACTIVE].code,
            );
            let buyFormValid = this.$store.getters[DYNAMIC_ESS.BUY_VALID];
            if (gridFormValid && batteryFormValid && buyFormValid) {
                this.creating = false;
                this.$emit('creating', false);
            }
        }
        this.changedField.set(
            field,
            this.initialValues[field] === null ||
                newValue.toString() !== this.initialValues[field].toString(),
        );
        this.changedField.forEach(value => {
            hasChange = hasChange || value;
        });
        if (this.creating) {
            this.$emit('creating', true);
        }
        this.$emit('change', {
            hasChange: hasChange,
            formName: 'grid',
        });
    }
}
</script>
